import { Box, Button, Menu, MenuItem, SxProps, Theme } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { DateRange, RangeKeyDict } from "react-date-range";
import { DATE_FORMAT } from "../../constants";
import Select from "../Select";

interface IDateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  sx?: SxProps<Theme>;
  onChange?: (startDate: Date, endDate: Date) => void;
}

type TPeriod =
  | "custom"
  | "last_7_days"
  | "last_30_days"
  | "last_year"
  | "lifetime";

type TPeriodOption = {
  label: string;
  value: TPeriod;
};

const PERIOD_OPTIONS: TPeriodOption[] = [
  { label: "Custom", value: "custom" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 30 days", value: "last_30_days" },
  { label: "Last year", value: "last_year" },
  { label: "Lifetime", value: "lifetime" },
];

export const DateRangePicker: React.FC<IDateRangePickerProps> = ({
  startDate = moment().subtract(1, "month").toDate(),
  endDate = new Date(),
  sx,
  onChange,
}) => {
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [period, setPeriod] = useState<TPeriod>("last_30_days");

  const isMenuOpened = Boolean(menuEl);

  const handleDateRangeChange = (item: RangeKeyDict) => {
    if (item.selection && item.selection.startDate && item.selection.endDate) {
      onChange?.(item.selection.startDate, item.selection.endDate);
      setPeriod("custom");
    }
  };

  const handlePeriodChange = (value: TPeriod) => {
    const newEndDate = moment().toDate();

    let newStartDate = startDate;

    switch (value) {
      case "last_7_days":
        newStartDate = moment().subtract(6, "days").toDate();
        break;
      case "last_30_days":
        newStartDate = moment().subtract(1, "month").toDate();
        break;
      case "last_year":
        newStartDate = moment().subtract(1, "year").toDate();
        break;
      case "lifetime":
        newStartDate = new Date(0); // January 1, 1970
        break;
    }

    setPeriod(value);
    onChange?.(newStartDate, newEndDate);
  };

  return (
    <>
      <Button
        id="date-range-picker-button"
        aria-controls={isMenuOpened ? "date-range-picker-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpened ? "true" : undefined}
        onClick={(e) => setMenuEl(e.currentTarget)}
        variant="outlined"
        size="large"
        color="default"
        sx={sx}
      >
        {moment(startDate).format(DATE_FORMAT)} -{" "}
        {moment(endDate).format(DATE_FORMAT)}
      </Button>
      <Menu
        id="date-range-picker-menu"
        anchorEl={menuEl}
        open={isMenuOpened}
        onClose={() => setMenuEl(null)}
        slotProps={{
          list: {
            "aria-labelledby": "date-range-picker-button",
          },
        }}
      >
        <Box px={1} pb={1}>
          <Select
            fullWidth
            value={period}
            onChange={(e) => handlePeriodChange(e.target.value as TPeriod)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.value === "custom"}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <DateRange
          editableDateInputs={true}
          moveRangeOnFirstSelection={false}
          maxDate={new Date()}
          onChange={handleDateRangeChange}
          ranges={[
            {
              startDate,
              endDate,
              key: "selection",
            },
          ]}
        />
      </Menu>
    </>
  );
};

export default DateRangePicker;
